import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, FileText, Play } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { SupportAttachment } from '../../../common/models/support.model';
import { formatBytes } from '../support-meta';

/** A message's files: image thumbnails that open full size, inline video, and file chips. */
export function AttachmentView({ attachments, align = 'start' }: { attachments: SupportAttachment[]; align?: 'start' | 'end' }) {
  const { t } = useTranslation();
  const [viewing, setViewing] = useState<SupportAttachment | null>(null);
  if (!attachments?.length) return null;

  const images = attachments.filter((a) => a.kind === 'image');
  const videos = attachments.filter((a) => a.kind === 'video');
  const files = attachments.filter((a) => a.kind === 'file');

  return (
    <div className={cn('flex max-w-[85%] flex-col gap-1.5', align === 'end' ? 'self-end items-end' : 'self-start items-start')}>
      {images.length > 0 && (
        <div className={cn('grid gap-1.5', images.length === 1 ? 'grid-cols-1' : 'grid-cols-2')}>
          {images.map((a) => (
            <button
              key={a.url}
              type="button"
              onClick={() => setViewing(a)}
              className="group relative overflow-hidden rounded-xl border bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={t('SUPPORT.ATTACHMENT.OPEN_IMAGE', { name: a.name })}
            >
              <img
                src={a.url}
                alt={a.name}
                loading="lazy"
                className={cn('object-cover transition-transform group-hover:scale-[1.02]', images.length === 1 ? 'max-h-56 w-auto max-w-full' : 'size-28')}
              />
            </button>
          ))}
        </div>
      )}
      {videos.map((a) => (
        <video
          key={a.url}
          src={a.url}
          controls
          preload="metadata"
          className="max-h-64 w-full max-w-sm rounded-xl border bg-black"
          aria-label={a.name}
        />
      ))}
      {files.map((a) => (
        <a
          key={a.url}
          href={a.url}
          target="_blank"
          rel="noreferrer"
          className="flex max-w-full items-center gap-2 rounded-xl border bg-card px-3 py-2 text-xs transition-colors hover:bg-muted"
        >
          <FileText className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 truncate font-medium">{a.name}</span>
          <span className="shrink-0 text-muted-foreground">{formatBytes(a.size)}</span>
          <Download className="size-3.5 shrink-0 text-muted-foreground" />
        </a>
      ))}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-[min(92vw,1100px)] border-none bg-transparent p-0 shadow-none sm:max-w-[min(92vw,1100px)]" showCloseButton>
          <DialogTitle className="sr-only">{viewing?.name}</DialogTitle>
          {viewing && (
            <div className="flex flex-col items-center gap-2">
              <img src={viewing.url} alt={viewing.name} className="max-h-[80vh] w-auto rounded-xl" />
              <a href={viewing.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white">
                <Download className="size-3.5" />
                {viewing.name}
              </a>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Small preview used in the composer tray. */
export function AttachmentThumb({ file, url }: { file: { name: string; type: string }; url?: string }) {
  if (file.type.startsWith('image/') && url) return <img src={url} alt="" className="size-full object-cover" />;
  if (file.type.startsWith('video/')) return <Play className="size-4" />;
  return <FileText className="size-4" />;
}
